import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  IconButton,
  Box,
} from "@mui/material";
import {
  LuChevronsLeft,
  ExpandLess,
  ExpandMore,
} from "@/Components/Icons/Icons";
import { sidebarMenu } from "./sidebarMenu";
import { useNavigate } from "react-router";
import { apiurl } from "@/Config/ApiConfig/ApiUrl";
import { useGlobalFilter } from "../../Context/GlobalFilterContext";

const drawerWidthOpen = 240;
const drawerWidthClosed = 72;

export default function SidebarDrawer() {
  const [expanded, setExpanded] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const { clearFilters } = useGlobalFilter();
  const [selectedChild, setSelectedChild] = useState(() => {
    const stored = localStorage.getItem("selectedChild");
    if (stored) {
      return JSON.parse(stored);
    }

    // Default to Export Job if nothing stored
    const parentItem = sidebarMenu.find((item) =>
      item.children?.some((child) => child.label === "Export Job")
    );
    return parentItem ? { [parentItem.label]: "Export Job" } : {};
  });

  useEffect(() => {
    localStorage.setItem("selectedChild", JSON.stringify(selectedChild));
  }, [selectedChild]);

  const handleChildClick = (parentLabel, childLabel) => {
    const newSelection = { parent: parentLabel, child: childLabel };
    setSelectedChild(newSelection);
    clearFilters();
    // Navigate
    switch (childLabel) {
      case "Export Job":
        navigate(apiurl.Dashboard);
        break;
      case "Import Job":
        navigate(apiurl.Importdashboard);
        break;
      default:
        navigate(`/${childLabel.toLowerCase().replace(/\s+/g, "-")}`);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (!drawerOpen) {
      setExpanded(null);
    }
  }, [drawerOpen]);

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  const handleExpand = (label) => {
    if (!drawerOpen) setDrawerOpen(true);
    setExpanded((prev) => (prev === label ? null : label));
  };

  const handleParentClick = (item) => {
    if (item.children?.length) {
      handleExpand(item.label);
    } else {
      setSelectedChild({ parent: item.label, child: null });
      setExpanded(false)

      // Route based on parent label
      switch (item.label) {
        case "Organization":
          navigate(apiurl.Organization_Dashboard);
          break;
        case "Airline":
          // navigate("/airline");
          break;
        case "Shipping Line":
          // navigate("/shipping-line");
          break;
        case "Reports":
          // navigate("/reports");
          break;
        case "My Tripath":
          // navigate("/my-tripath");
          break;
        case "ICE GATE":
          // navigate("/ice-gate");
          break;
        case "Tripath Integrations":
          // navigate("/integrations");
          break;
        case "Tools":
          // navigate("/tools");
          break;
        case "Administration":
          // navigate("/admin");
          break;
        default:
          navigate(`/${item.label.toLowerCase().replace(/\s+/g, "-")}`);
      }
    }
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerOpen ? drawerWidthOpen : drawerWidthClosed,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerOpen ? drawerWidthOpen : drawerWidthClosed,
          mt: "65px",
          overflowX: "hidden",
          backgroundColor: "var(--gray-0)",
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid var(--gray-100)",
          zIndex: 0, // Just below your dialog
        },
      }}
    >
      <List
        disablePadding
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {sidebarMenu.map((item) => {
          const Icon = item.icon;
          const hasChildren = item.children?.length > 0;
          const isExpanded = expanded === item.label;

          return (
            <React.Fragment key={item.label}>
              <ListItemButton
                onClick={() => handleParentClick(item)}
                sx={{
                  py: 0.5,
                  px: drawerOpen ? 1.5 : 1,
                  minHeight: "50px",
                  justifyContent: drawerOpen ? "flex-start" : "center",
                  color: item.children?.length
                    ? isExpanded
                      ? "var(--gray-600)"
                      : "var(--gray-600)"
                    : selectedChild.parent === item.label
                      ? "var(--gray-100)"
                      : "var(--gray-600)",
                  borderRadius: 1,
                  backgroundColor: item.children?.length
                    ? isExpanded
                      ? "var(--brand-100)"
                      : "transparent"
                    : selectedChild.parent === item.label &&
                        selectedChild.child === null
                      ? "var(--brand-600)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: item.children?.length
                      ? isExpanded
                        ? "var(--brand-100)"
                        : "transparent"
                      : selectedChild.parent === item.label &&
                          selectedChild.child === null
                        ? "var(--brand-600)"
                        : "var(--brand-100)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: drawerOpen ? 36 : 28,
                    backgroundColor:
                      selectedChild.parent === item.label && !drawerOpen
                        ? "var(--brand-600)"
                        : "",
                    justifyContent: "center",
                    borderRadius: 1,
                    color:
                    item.children?.length
                    ? isExpanded
                      ? "var(--gray-600)"
                      : "var(--gray-600)":
                      selectedChild.parent === item.label
                        ? "var(--gray-100)"
                        : "var(--gray-600)",

                    p: 1,
                  }}
                >
                  {Icon && <Icon size={16} />}
                </ListItemIcon>

                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: "bodySmall",
                      }}
                    />
                    {hasChildren &&
                      (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                  </>
                )}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      px: drawerOpen ? 1.5 : 3,
                      backgroundColor: "var(--brand-100)",
                    }}
                  >
                    {item.children.map((child) => {
                      const isSelected =
                        selectedChild.parent === item.label &&
                        selectedChild.child === child.label;

                      return (
                        <Box
                          key={child.label}
                          sx={{
                            borderRadius: 1,
                            backgroundColor: isSelected
                              ? "var(--brand-600)"
                              : "transparent",
                            color: isSelected
                              ? "var(--gray-0)"
                              : "var(--brand-150)",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "var(--brand-600)"
                                : "var(--brand-150)",
                              color: isSelected
                                ? "var(--gray-0)"
                                : "var(--gray-700)",
                            },
                          }}
                        >
                          <ListItemButton
                            onClick={() =>
                              handleChildClick(item.label, child.label)
                            }
                            sx={{
                              pl: 4,
                              py: 0.5,
                              minHeight: "40px",
                            }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: "14px",
                                sx: { fontWeight: isSelected ? 600 : 400 },
                              }}
                            />
                          </ListItemButton>
                        </Box>
                      );
                    })}
                  </List>
                </Collapse>
              )}

              {["My Tripath", "ICE GATE", "Tripath Integrations"].includes(
                item.label
              ) && <Divider sx={{ my: 0.5, mx: drawerOpen ? 2 : 1 }} />}
            </React.Fragment>
          );
        })}
      </List>

      <Divider />

      {/* Toggle Button */}
      <Box
        sx={{
          paddingBottom: 8,
          paddingTop: 1,
          display: "flex",
          ml: 2,
        }}
      >
        <IconButton color="inherit" onClick={toggleDrawer}>
          <LuChevronsLeft />
        </IconButton>
      </Box>
    </Drawer>
  );
}
